// The embed snippets for one inline placement, prefilled with its token.
// Behind a button so several inline placements don't repeat the same code
// on the page; the card keeps only what differs, the token.

import { Button, CopyButton, Dialog } from "@pej/kore/components";
import { mountSnippet, staticSnippet } from "./embed-snippets";

export function EmbedCodeDialog({
    name,
    token,
    open,
    onOpenChange,
}: {
    name: string;
    token: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}) {
    const origin = window.location.origin;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <Dialog.Content size="xl">
                <Dialog.Header>
                    <Dialog.Title>Embed {name}</Dialog.Title>
                    <Dialog.Description>
                        Both snippets carry this placement's embed reference, {token}.
                    </Dialog.Description>
                </Dialog.Header>
                {/* min-w-0: the popup is a grid, and its items won't shrink below
                    the snippets' longest line without it. */}
                <div className="flex min-w-0 flex-col gap-4">
                    <Snippet label="On a static page" value={staticSnippet(origin, token)} />
                    <Snippet label="From application code" value={mountSnippet(origin, token)} />
                    <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-kore-text-muted">
                        <li>
                            The context token is a JWT your server signs to say who the guest is and where. Leave it out
                            for anonymous answers, unless the workspace refuses unsigned context under Security.
                        </li>
                        <li>
                            When a guest finishes, <code>onSubmitted</code> (or the script tag's{" "}
                            <code>surveys:submitted</code> event) gets the response id, to link it to your own records.
                        </li>
                    </ul>
                </div>
                <Dialog.Footer>
                    <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                        Close
                    </Button>
                </Dialog.Footer>
            </Dialog.Content>
        </Dialog>
    );
}

function Snippet({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex min-w-0 flex-col gap-1">
            <p className="text-sm font-medium">{label}</p>
            <div className="flex items-start gap-2">
                <pre className="min-w-0 flex-1 overflow-x-auto rounded-kore-md border border-kore-border-soft bg-kore-bg p-3 text-xs">
                    {value}
                </pre>
                <CopyButton value={value} label={`Copy snippet: ${label.toLowerCase()}`} />
            </div>
        </div>
    );
}
